// libs
import * as React from 'react'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'

// utils
import { getCookieItem, setCookieItem } from 'src/utils/storage_wrapper/storage_wrapper'
import { getProviderDisplayName } from './provider_names'

// styles
import './preview_with_consent.scss'

interface IProps {
  type: 'video' | 'audio' | string
  source: 'youtube' | 'spotify' | 'apple_music' | string
  children: React.ReactNode
  providerName?: string
  className?: string
}

export const cls = `${CLASS_PREFIX}preview-with-consent__`

// Generate unique ID for accessibility
let idCounter = 0
const generateId = () => `preview-consent-${++idCounter}`

const PreviewWithConsent: React.FC<IProps> = ({ type, source, children, providerName, className }) => {
  const cookieKey = `${source}_consent_given`
  const [checkboxId] = React.useState(() => generateId())
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  const [hasConsent, setHasConsent] = React.useState<boolean>(() => {
    const consent = getCookieItem(cookieKey)
    return consent === 'true'
  })
  const [rememberConsent, setRememberConsent] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!hasConsent && buttonRef.current) {
      buttonRef.current.focus()
    }
  }, [hasConsent])

  const handleConsent = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()

    if (rememberConsent) {
      setCookieItem(cookieKey, 'true')
    }
    setHasConsent(true)
  }

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent NavigationItem from capturing clicks on the consent dialog
    e.stopPropagation()
  }

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRememberConsent(event.target.checked)
  }

  if (!children) {
    return null
  }

  if (!hasConsent) {
    const displayName = getProviderDisplayName(source, providerName)
    const consentMessage = `Do you want to load external content supplied by ${displayName}?`
    const rememberLabel = 'Remember this choice'
    const acceptLabel = 'Yes'

    return (
      <div
        className={className || cls.slice(0, -2)}
        data-content-type={type}
        data-source={source}
        onClick={handleContainerClick}
      >
        <div
          className={cls + 'consent'}
          role="dialog"
          aria-labelledby={`${checkboxId}-message`}
          aria-describedby={`${checkboxId}-description`}
        >
          <p
            id={`${checkboxId}-message`}
            className={cls + 'consent-text'}
          >
            {consentMessage}
          </p>
          <div className={cls + 'consent-options'}>
            <label
              htmlFor={checkboxId}
              className={cls + 'consent-checkbox-label'}
            >
              <input
                id={checkboxId}
                type="checkbox"
                checked={rememberConsent}
                onChange={handleCheckboxChange}
                className={cls + 'consent-checkbox'}
                aria-label={rememberLabel}
              />
              {rememberLabel}
            </label>
            <button
              ref={buttonRef}
              className={cls + 'consent-button'}
              onClick={handleConsent}
              aria-label={`Load external content from ${displayName}`}
            >
              {acceptLabel}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default PreviewWithConsent
